import React, { useState, useEffect } from 'react'
import mammoth from 'mammoth'
import {
  Button,
  FilePreviewPanel as DsFilePreviewPanel,
  type FilePreviewContent,
} from '@enamelai/design-system'
import {
  formatFileDate,
  getFileTypeFromFileName,
} from '@components/pages/Files/filesAndLetters.util'
import type { MockFileItem } from '@pages/Files/mockFilesData'
import { sanitizeDocumentHtml } from '@utils/sanitize-html.util'
import './FilePreviewPanel.css'

export type FilePreviewPanelProps = {
  file?: MockFileItem | null
  isFullscreen: boolean
  onToggleFullscreen: () => void
  onClose: () => void
  onDelete: () => void
  onDownload: () => void
  onDetails: () => void
  onPrev?: () => void
  onNext?: () => void
  hasPrev?: boolean
  hasNext?: boolean
}

const ZOOM_STEP = 25
const ZOOM_MIN = 25
const ZOOM_MAX = 300

const FilePreviewPanel: React.FC<FilePreviewPanelProps> = ({
  file,
  isFullscreen,
  onToggleFullscreen,
  onClose,
  onDelete,
  onDownload,
  onDetails,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}) => {
  const [zoom, setZoom] = useState(100)
  const [docHtml, setDocHtml] = useState<string | null>(null)
  const [docError, setDocError] = useState<string | null>(null)
  const [docLoading, setDocLoading] = useState(false)
  const fileType = file ? getFileTypeFromFileName(file.fileName) : 'other'

  const isDocument = !!file && fileType === 'document'
  useEffect(() => {
    if (!isDocument || !file?.previewUrl) {
      setDocHtml(null)
      setDocError(null)
      setDocLoading(false)
      return
    }
    let cancelled = false
    setDocLoading(true)
    setDocError(null)
    setDocHtml(null)
    fetch(file.previewUrl)
      .then((res) => res.arrayBuffer())
      .then((arrayBuffer) => mammoth.convertToHtml({ arrayBuffer }))
      .then((result) => {
        if (!cancelled) {
          setDocHtml(sanitizeDocumentHtml(result.value))
          setDocError(null)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDocError(err?.message ?? 'Failed to load document')
          setDocHtml(null)
        }
      })
      .finally(() => {
        if (!cancelled) setDocLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isDocument, file?.id, file?.previewUrl])

  const content: FilePreviewContent = ((): FilePreviewContent => {
    if (!file) return { kind: 'empty' }
    const placeholder: FilePreviewContent = {
      kind: 'placeholder',
      fileType,
      fileName: file.fileName,
    }
    if (!file.previewUrl) return placeholder
    if (fileType === 'pdf') return { kind: 'pdf', src: file.previewUrl }
    if (fileType === 'image') {
      return { kind: 'image', src: file.previewUrl, alt: file.fileName }
    }
    if (fileType === 'document') {
      if (docLoading) return { kind: 'document', loading: true }
      if (docError) {
        return {
          kind: 'document',
          error: `Preview not available. ${docError}`,
        }
      }
      if (docHtml) {
        return { kind: 'document', html: sanitizeDocumentHtml(docHtml) }
      }
    }
    return placeholder
  })()

  const meta = file
    ? [formatFileDate(file.uploadDate), file.fileSize && `(${file.fileSize})`]
        .filter(Boolean)
        .join(' - ')
    : undefined

  return (
    <DsFilePreviewPanel
      className={`fal-preview-panel${isFullscreen ? ' fal-preview-panel-fullscreen' : ''}`}
      fileName={file?.fileName}
      meta={meta}
      content={content}
      zoom={zoom}
      hasPrev={hasPrev}
      hasNext={hasNext}
      onClose={onClose}
      onDelete={onDelete}
      onDownload={onDownload}
      onDetails={onDetails}
      onZoomIn={() => setZoom((prev) => Math.min(prev + ZOOM_STEP, ZOOM_MAX))}
      onZoomOut={() => setZoom((prev) => Math.max(prev - ZOOM_STEP, ZOOM_MIN))}
      onPrev={onPrev}
      onNext={onNext}
      headerActions={
        <Button
          variant="ghost"
          size="sm"
          className="fal-preview-fullscreen"
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          onClick={onToggleFullscreen}
        >
          <i
            className={`mgc ${isFullscreen ? 'mgc_fullscreen_exit_line' : 'mgc_fullscreen_line'}`}
            aria-hidden
          />
        </Button>
      }
    />
  )
}

export default FilePreviewPanel
